import type { DatoveSchrankyDatovaZpravaDto } from '@gov-nx/api';
import {
	archiveAllDataBoxesMessagesQuery,
	archiveDataBoxesMessagesQuery,
	deleteAllDataboxMessages,
	deleteDataBoxesMessagesQuery,
	restoreDataBoxesMessagesQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useDataBoxEvents, useMessageEvents } from '@gov-nx/core/events';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { isFulfilled, isRejected } from '@gov-nx/core/types';
import { PageCode } from '@gov-nx/module/page';
import type {
	DataBoxConfirmModalType,
	DataBoxFolderType,
} from '../providers/DataBoxes.types';
import { DataBoxMessageListLoadingItemsType } from '../providers/MessageListContext.types';
import { useDataBoxMatomo } from './useDataBoxMatomo';

interface MassOperationsProps {
	messageList: DatoveSchrankyDatovaZpravaDto[];
	selectedMessageList: number[];
	dataBoxId: string;
	folderType: DataBoxFolderType;
	updateLoadingItems: (
		loadingItem: DataBoxMessageListLoadingItemsType,
		add: boolean
	) => void;
	setConfirmModal: (confirmModal: Nullable<DataBoxConfirmModalType>) => void;
	cancelMassOperations: () => void;
}

export const useMassOperations = ({
	messageList,
	selectedMessageList,
	dataBoxId,
	folderType,
	updateLoadingItems,
	setConfirmModal,
	cancelMassOperations,
}: MassOperationsProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { trackEvent } = useDataBoxMatomo();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);

	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const {
		messagesArchive,
		messagesUnselectAll,
		messageListUpdate,
		storageRefetch,
	} = useDataBoxEvents();
	const { axiosInstance } = useAxiosInstance();

	const archiveMessages = usePoMutation({
		mutationFn: archiveDataBoxesMessagesQuery(axiosInstance),
		onSuccess: (data, variables) => {
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, false);
			messagesUnselectAll();
			cancelMassOperations();
			storageRefetch();

			messagesArchive({
				messageIds: variables.messagesIds.filter(
					(_, index) => data[index].status === 'fulfilled'
				),
			});

			const successCount = data.filter(isFulfilled).length;
			const errorResponses = data.filter(isRejected);

			if (errorResponses.length) {
				trackEvent({
					action: 'bulk_archiving_error',
					value: errorResponses.length,
				});
				toastMessageError(
					tn('akce.chyba-archivace', { count: errorResponses.length })
				);
			} else {
				trackEvent({
					action: 'bulk_archiving_success',
					value: successCount,
				});
				toastMessageSuccess(
					tn('akce.zkopirovany-archiv', { count: successCount }),
					{
						icon: {
							name: 'archived-outline',
							type: 'templates',
						},
					}
				);
			}
		},
		onError: async () => {
			trackEvent({
				action: 'bulk_archiving_error',
			});
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, false);
		},
	});

	const handleArchiveMessages = async () => {
		updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, true);

		const messages = messageList.filter(
			(message) =>
				selectedMessageList.includes(message.datovaZpravaId as number) &&
				!message.datumArchivace
		);

		if (!messages.length) {
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, false);
			messagesUnselectAll();
			cancelMassOperations();

			toastMessageSuccess(
				tn('akce.zkopirovany-archiv', {
					count: selectedMessageList.length,
				}),
				{
					icon: {
						name: 'archived-outline',
						type: 'templates',
					},
				}
			);
		} else {
			archiveMessages.mutate({
				dataBoxId,
				messagesIds: messages.map(
					(message) => message.datovaZpravaId as number
				),
			});
		}
	};

	const archiveAllMessages = usePoMutation({
		mutationFn: archiveAllDataBoxesMessagesQuery(axiosInstance),
		onSuccess: () => {
			messagesArchive({
				messageIds: messageList.map(
					(message) => message.datovaZpravaId as number
				),
			});
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, false);
			storageRefetch();

			trackEvent({
				action: 'all_archiving_success',
			});
			toastMessageSuccess(tn('akce.vsechny-zpravy-archivovany'), {
				icon: {
					name: 'archived-outline',
					type: 'templates',
				},
			});
		},
		onError: async () => {
			trackEvent({
				action: 'all_archiving_error',
			});
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, false);
		},
	});

	const handleArchiveAllMessages = () => {
		updateLoadingItems(DataBoxMessageListLoadingItemsType.Archive, true);
		archiveAllMessages.mutate({ dataBoxId, folderType });
	};

	const deleteMessages = usePoMutation({
		mutationFn: deleteDataBoxesMessagesQuery(axiosInstance),
		onSuccess: (_data, { permanently, messagesIds }) => {
			updateLoadingItems(
				permanently
					? DataBoxMessageListLoadingItemsType.RemovePermanently
					: DataBoxMessageListLoadingItemsType.Remove,
				false
			);
			messageListUpdate();
			setConfirmModal(null);
			messagesUnselectAll();
			cancelMassOperations();
			storageRefetch();

			const successCount = messagesIds.length;

			trackEvent({
				action: 'empty_trash_success',
			});
			toastMessageSuccess(
				permanently
					? tn('akce.trvale-smazany-kopie', { count: successCount })
					: tn('akce.smazany-kopie', { count: successCount }),
				{
					icon: {
						name: 'trash',
						type: 'templates',
					},
				}
			);
		},
		onError: async (_, { permanently }) => {
			trackEvent({
				action: 'empty_trash_error',
			});
			updateLoadingItems(
				permanently
					? DataBoxMessageListLoadingItemsType.RemovePermanently
					: DataBoxMessageListLoadingItemsType.Remove,
				false
			);
		},
	});

	const handleDeleteMessages = async (permanently = false) => {
		updateLoadingItems(
			permanently
				? DataBoxMessageListLoadingItemsType.RemovePermanently
				: DataBoxMessageListLoadingItemsType.Remove,
			true
		);
		deleteMessages.mutate({
			messagesIds: selectedMessageList,
			permanently,
		});
	};

	const handleEmptyTrash = async () => {
		updateLoadingItems(
			DataBoxMessageListLoadingItemsType.RemovePermanently,
			true
		);
		deleteMessages.mutate({
			messagesIds: messageList.map(
				(message) => message.datovaZpravaId as number
			),
			permanently: true,
		});
	};

	const deleteAllMessagesMutation = usePoMutation({
		mutationFn: deleteAllDataboxMessages(axiosInstance),
		onSuccess: (_, variables) => {
			const successMessageText = variables.tvrde
				? tn('akce.vsechny-zpravy-trvale-smazany')
				: tn('akce.vsechny-zpravy-smazany');
			updateLoadingItems(
				variables.tvrde
					? DataBoxMessageListLoadingItemsType.RemovePermanently
					: DataBoxMessageListLoadingItemsType.Remove,
				false
			);
			messageListUpdate();
			setConfirmModal(null);
			messagesUnselectAll();
			cancelMassOperations();
			storageRefetch();

			trackEvent({
				action: 'delete_all_success',
			});
			toastMessageSuccess(successMessageText, {
				icon: {
					name: 'trash',
					type: 'templates',
				},
			});
		},
		onError: async (_, variables) => {
			trackEvent({
				action: 'delete_all_error',
			});
			updateLoadingItems(
				variables.tvrde
					? DataBoxMessageListLoadingItemsType.RemovePermanently
					: DataBoxMessageListLoadingItemsType.Remove,
				false
			);
		},
	});

	const handleDeleteAllMessages = async ({
		permanently,
	}: {
		permanently: boolean;
	}) => {
		updateLoadingItems(
			permanently
				? DataBoxMessageListLoadingItemsType.RemovePermanently
				: DataBoxMessageListLoadingItemsType.Remove,
			true
		);
		deleteAllMessagesMutation.mutate({
			datovaSchrankaId: dataBoxId,
			tvrde: permanently,
		});
	};

	const restoreMessages = usePoMutation({
		mutationFn: restoreDataBoxesMessagesQuery(axiosInstance),
		onSuccess: (data) => {
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Restore, false);
			messageListUpdate();
			messagesUnselectAll();
			cancelMassOperations();

			const successCount = data.filter(isFulfilled).length;
			const errorResponses = data.filter(isRejected);

			if (errorResponses.length) {
				trackEvent({
					action: 'bulk_restoring_error',
					value: errorResponses.length,
				});
				toastMessageError(
					tn('akce.chyba-obnoveni', { count: errorResponses.length })
				);
			} else {
				trackEvent({
					action: 'bulk_restoring_success',
					value: successCount,
				});
				toastMessageSuccess(tn('akce.obnoveny', { count: successCount }), {
					icon: {
						name: 'trash-restore',
						type: 'templates',
					},
				});
			}
		},
		onError: async () => {
			trackEvent({
				action: 'bulk_restoring_error',
			});
			updateLoadingItems(DataBoxMessageListLoadingItemsType.Restore, false);
		},
	});

	const handleRestoreMessages = async () => {
		updateLoadingItems(DataBoxMessageListLoadingItemsType.Restore, true);
		restoreMessages.mutate({ messagesIds: selectedMessageList });
	};

	return {
		handleArchiveMessages,
		handleArchiveAllMessages,
		handleDeleteMessages,
		handleDeleteAllMessagesPermanently: () =>
			handleDeleteAllMessages({ permanently: true }),
		handleDeleteAllMessages: () =>
			handleDeleteAllMessages({ permanently: false }),
		handleRestoreMessages,
		handleEmptyTrash,
	};
};
